import { useRef, useState } from 'react'
import { AxiosResponse } from 'axios'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, useQuery } from '@percent/admin-dashboard/common/hooks'
import { PBPGetResponse } from '@percent/admin-dashboard/services/pbpResponses'
import type {
  DonationRestrictedCountriesConfigValue,
  GivingDatePeriodConfigurationValue,
  GivingFeaturedConfigValue,
  GivingHelpLinkConfigValue,
  GivingHomePageBannerContentConfigValue,
  GivingRegionsConfigValue,
  GivingSpotlightedOrgsConfigValue,
  GivingThemeConfigValue,
  PartnerConfigRecord,
  VolunteeringAllowanceConfigValue
} from '@percent/admin-dashboard/api/types'

const HTTP_NOT_FOUND = 404

function useConfigQuery<T>(
  queryFn: (params: { partnerId: string }) => Promise<AxiosResponse<PBPGetResponse<PartnerConfigRecord<T>>>>,
  partnerId: string,
  skip: boolean
) {
  const [{ data, isLoading, errorMessage, status }, { refresh }] = useQuery(queryFn, { partnerId }, { skip })

  return {
    data: data?.value,
    isLoading,
    error: status === HTTP_NOT_FOUND ? undefined : errorMessage || undefined,
    refresh
  }
}

export function usePartnerGivingConfigs(partnerId: string, skip = false, hasVolunteeringScope = true) {
  const { adminService } = useServices()

  const [savingKey, setSavingKey] = useState<string | null>(null)

  // Refs (not state) so the mutation's onSuccess reads the latest refresh/callback without re-subscribing.
  const givingRefreshRef = useRef<(() => void) | null>(null)
  const givingSuccessCallbackRef = useRef<(() => void) | null>(null)

  const theme = useConfigQuery<GivingThemeConfigValue>(
    adminService.queries.getPartnerGivingThemeConfig,
    partnerId,
    skip
  )
  const currencies = useConfigQuery<string[]>(
    adminService.queries.getPartnerGivingSupportedCurrenciesConfig,
    partnerId,
    skip
  )
  const bannerContent = useConfigQuery<GivingHomePageBannerContentConfigValue>(
    adminService.queries.getPartnerGivingHomePageBannerContentConfig,
    partnerId,
    skip
  )
  const regions = useConfigQuery<GivingRegionsConfigValue>(
    adminService.queries.getPartnerGivingRegionsConfig,
    partnerId,
    skip
  )
  const calendarIntegration = useConfigQuery<string[]>(
    adminService.queries.getPartnerGivingCalendarIntegrationConfig,
    partnerId,
    skip
  )
  const helpLink = useConfigQuery<GivingHelpLinkConfigValue>(
    adminService.queries.getPartnerGivingHelpLinkConfig,
    partnerId,
    skip
  )
  const preferredCountries = useConfigQuery<string[]>(
    adminService.queries.getPartnerGivingPreferredCountriesConfig,
    partnerId,
    skip
  )
  const requireOrganisationTimeLog = useConfigQuery<boolean>(
    adminService.queries.getPartnerGivingRequireOrganisationTimeLogConfig,
    partnerId,
    skip
  )
  const spotlightedOrgs = useConfigQuery<GivingSpotlightedOrgsConfigValue>(
    adminService.queries.getPartnerGivingSpotlightedOrgsConfig,
    partnerId,
    skip
  )
  const featured = useConfigQuery<GivingFeaturedConfigValue>(
    adminService.queries.getPartnerGivingFeaturedConfig,
    partnerId,
    skip
  )
  const restrictedCountriesForDonation = useConfigQuery<DonationRestrictedCountriesConfigValue>(
    adminService.queries.getPartnerDonationRestrictedCountriesConfig,
    partnerId,
    skip
  )
  const datePeriodConfiguration = useConfigQuery<GivingDatePeriodConfigurationValue>(
    adminService.queries.getPartnerGivingDatePeriodConfig,
    partnerId,
    skip
  )
  const volunteeringAllowance = useConfigQuery<VolunteeringAllowanceConfigValue>(
    adminService.queries.getPartnerVolunteeringAllowanceConfig,
    partnerId,
    skip || !hasVolunteeringScope
  )
  const achievementResetDate = useConfigQuery<string>(
    adminService.queries.getPartnerGamificationAchievementResetDateConfig,
    partnerId,
    skip
  )

  const [
    { isLoading: givingMutating, errorMessage: givingMutationError },
    { apiFunc: updateGiving, setErrorMessage: resetGivingError }
  ] = useMutation(
    adminService.commands.updatePartnerGivingConfig,
    () => {
      givingRefreshRef.current?.()
      givingRefreshRef.current = null
      givingSuccessCallbackRef.current?.()
      givingSuccessCallbackRef.current = null
      setSavingKey(null)
    },
    // onError: clear only the refs, NOT savingKey — keeping savingKey set lets errorForKey
    // surface the failure on the active row until the user cancels/closes (resetSaving).
    () => {
      givingRefreshRef.current = null
      givingSuccessCallbackRef.current = null
    }
  )

  // Intentional global save lock: one in-flight save at a time, so every row's Save is disabled while any save runs.
  const isSaving = givingMutating

  const mutationError = givingMutationError || undefined

  // Clears the in-flight key and any settled error on cancel/close so failed-then-dismissed state can't linger.
  const resetSaving = () => {
    setSavingKey(null)
    resetGivingError('')
  }

  const saveGiving = (
    key: Parameters<typeof updateGiving>[0]['key'],
    value: Parameters<typeof updateGiving>[0]['value'],
    refresh: () => void,
    onSuccess?: () => void
  ) => {
    setSavingKey(key)
    resetGivingError('')
    givingRefreshRef.current = refresh
    givingSuccessCallbackRef.current = onSuccess ?? null
    updateGiving({ partnerId, key, value })
  }

  return {
    theme,
    currencies,
    bannerContent,
    regions,
    calendarIntegration,
    helpLink,
    preferredCountries,
    requireOrganisationTimeLog,
    spotlightedOrgs,
    featured,
    restrictedCountriesForDonation,
    datePeriodConfiguration,
    volunteeringAllowance,
    achievementResetDate,

    isSaving,
    savingKey,
    mutationError,
    resetSaving,

    saveTheme: (value: GivingThemeConfigValue, onSuccess?: () => void) =>
      saveGiving('theme', value, theme.refresh, onSuccess),
    saveBannerContent: (value: GivingHomePageBannerContentConfigValue, onSuccess?: () => void) =>
      saveGiving('home_page_banner_content', value, bannerContent.refresh, onSuccess),
    saveCalendarIntegration: (value: string[], onSuccess?: () => void) =>
      saveGiving('calendar_integration', value, calendarIntegration.refresh, onSuccess),

    saveHelpLink: (value: GivingHelpLinkConfigValue, onSuccess?: () => void) =>
      saveGiving('help_link', value, helpLink.refresh, onSuccess),
    saveRequireOrganisationTimeLog: (value: boolean, onSuccess?: () => void) =>
      saveGiving('require_organisation_time_log', value, requireOrganisationTimeLog.refresh, onSuccess)
  }
}
